/**
 * Statuts métier, repris littéralement du document "Base de données" (§3.x) pour
 * garder une traçabilité directe avec le cahier des charges fourni par la CNSS.
 */
export const EMPLOYEUR_STATUTS = [
    'actif',
    'suspendu',
    'inactif',
    'en_attente_validation',
    'archive_logique',
];
export const TRAVAILLEUR_STATUTS = [
    'en_attente',
    'valide',
    'immatricule',
    'suspendu',
    'rejete',
    'fusionne',
    'archive_logique',
];
export const DOCUMENT_STATUTS_VERIFICATION = [
    'en_attente',
    'valide',
    'invalide',
    'illisible',
    'manquant',
    'a_completer',
];
export const DOCUMENT_TYPES = [
    'carte_identite',
    'passeport',
    'acte_naissance',
    'acte_mariage',
    'jugement_suppletif',
    'attestation_employeur',
    'photo_passeport',
    'preuve_regularite_sejour',
    'autorisation_travail',
    'autre',
];
/**
 * Sous-ensemble de `DOCUMENT_TYPES` qui établit l'identité d'une personne.
 *
 * Sert de condition à l'orientation vers la biométrie : le poste d'accueil ne
 * peut envoyer quelqu'un en capture biométrique qu'après avoir vérifié et
 * validé au moins une de ces pièces. Une attestation d'employeur ou une photo
 * d'identité, par exemple, ne prouvent pas qui est la personne — les inclure
 * viderait le contrôle de son sens.
 */
export const DOCUMENT_TYPES_IDENTITE = [
    'carte_identite',
    'passeport',
    'acte_naissance',
    'jugement_suppletif',
];
export const BIOMETRIE_STATUTS = ['active', 'remplacee', 'invalide', 'archivee'];
/**
 * File d'attente entre le poste d'accueil et le poste biométrique.
 *
 * `en_attente` : la personne a été reçue, son identité vérifiée, elle attend
 * son tour à la capture. `traitee` : la capture est faite, elle sort de la
 * file. `annulee` : la personne est repartie sans être biométrée (départ,
 * pièce finalement rejetée, erreur de saisie) — tracé plutôt que supprimé,
 * pour que la file du jour reste analysable a posteriori.
 */
export const FILE_ATTENTE_STATUTS = ['en_attente', 'traitee', 'annulee'];
export const BIOMETRIE_TYPES = ['empreinte', 'visage', 'signature'];
export const ABIS_RESULTATS = [
    'aucun_doublon',
    'doublon_suspect',
    'doublon_confirme',
    'erreur_technique',
];
/** Décisions humaines possibles sur une trace ABIS en file de revue (Sprint 3). */
export const ABIS_DECISIONS = ['valider', 'rejeter', 'suspendre', 'escalader'];
export const CARTE_STATUTS = [
    'en_stock',
    'affectee',
    'personnalisee',
    'en_impression',
    'echec_impression',
    'prete',
    'expediee',
    'recue_agence',
    'remise',
    'active',
    'suspendue',
    'expiree',
    'remplacee',
    'annulee',
];
/**
 * Transitions autorisées du cycle de vie de la carte — appliquées côté API (Sprint 4).
 *
 * `en_impression`/`echec_impression` (ajout ultérieur, voir PROGRESS.md —
 * "Impression par lot") : chemin ALTERNATIF et optionnel à
 * `personnalisee → prete`, pour l'impression par lot non surveillée
 * (`apps/api/src/modules/impression/`). Le chemin direct existant
 * (`personnalisee → prete` via `POST /cartes/:uuid/marquer-prete`) reste
 * inchangé pour l'impression ponctuelle carte par carte — les deux
 * coexistent, une carte donnée n'emprunte que l'un ou l'autre selon qu'elle a
 * été mise en lot d'impression ou non.
 *
 * Note sur le cast `'non_conforme' as CarteStatut` retiré de la transition
 * `remise` (présent depuis le tout premier commit du projet, Sprint 0) :
 * investigation faite en préparant ce module. `'non_conforme'` n'a jamais été
 * un membre de `CARTE_STATUTS` — seulement de `REMISE_STATUTS` ci-dessous
 * (`remise_carte.statut_remise`, une colonne de la table `RemiseCarte`, pas
 * de `CarteSociale.statutCarte`). Le cast servait donc uniquement à faire
 * taire TypeScript sur une valeur qui n'a jamais pu être atteinte au runtime
 * (`applyCarteTransition` ne peut lire un `statutCarte` littéral
 * "non_conforme" que si un code l'y a écrit un jour, et aucun ne l'a jamais
 * fait — `POST /cartes/:uuid/remettre`, seule route qui écrit `RemiseCarte`,
 * fige `statutRemise: 'remise'` en dur, voir
 * `apps/api/src/modules/cartes/cartes.routes.ts`). C'est un vestige d'une
 * intention de conception du Sprint 0 (rejeter une remise dont l'identité ne
 * correspond pas, en faisant alors passer la CARTE elle-même à un statut
 * "non conforme") jamais implémentée : le sprint qui a construit `remettre`
 * a choisi de ne tracer cet éventuel refus que sur `RemiseCarte.statutRemise`
 * (qui a bien `'refusee'`/`'ajournee'`/`'non_conforme'`), sans jamais faire
 * régresser le statut de la carte elle-même — et n'a pas nettoyé la ligne
 * laissée dans `CARTE_TRANSITIONS`. Un vrai trou de sécurité de type (une
 * transition listée comme "autorisée" qui ne peut jamais être demandée
 * légitimement, ce qui aurait pu masquer un bug futur écrivant ce statut par
 * erreur ailleurs) plutôt qu'une fonctionnalité incomplète à finir : retiré
 * plutôt que résurrecté, faute de toute demande CNSS pour un statut de carte
 * "non conforme" distinct de ce que `RemiseCarte.statutRemise` trace déjà, et
 * hors du périmètre de ce sprint (impression par lot) pour construire ce
 * chemin de refus de remise au complet (route dédiée, écran, permission).
 */
export const CARTE_TRANSITIONS = {
    en_stock: ['affectee', 'annulee'],
    affectee: ['personnalisee', 'annulee'],
    personnalisee: ['prete', 'en_impression', 'annulee'],
    en_impression: ['prete', 'echec_impression'],
    echec_impression: ['en_impression', 'annulee'],
    prete: ['expediee', 'annulee'],
    expediee: ['recue_agence'],
    recue_agence: ['remise'],
    remise: ['active'],
    active: ['suspendue', 'expiree', 'remplacee'],
    suspendue: ['active', 'remplacee', 'annulee'],
    expiree: ['remplacee'],
    remplacee: [],
    annulee: [],
};
export const LOT_CARTE_STATUTS = [
    'recu',
    'en_stock',
    'en_transit',
    'cloture',
    'en_anomalie',
];
/**
 * `LotImpression` (module impression par lot, voir PROGRESS.md — "Impression
 * par lot") : `en_attente` avant tout traitement (transitoire, le lot passe
 * `en_cours` dès sa création dans la même transaction), `en_cours` tant qu'au
 * moins une carte du lot est encore `en_impression`, puis `termine` (toutes
 * les cartes du lot sont `prete`) ou `termine_avec_echecs` (au moins une
 * `echec_impression` restante) une fois le traitement achevé — recalculé à
 * chaque fois qu'une carte du lot se règle (voir
 * `apps/api/src/modules/impression/`).
 */
export const LOT_IMPRESSION_STATUTS = ['en_attente', 'en_cours', 'termine', 'termine_avec_echecs'];
export const MOUVEMENT_TYPES = ['entree', 'sortie', 'transfert', 'retour', 'destruction'];
/**
 * `mouvement_logistique.statut_mouvement` (Sprint 5) est une colonne texte
 * libre dans le schéma fourni, sans enum dédié — valeurs choisies ici et
 * documentées dans PROGRESS.md, sur le même principe que `REMISE_STATUTS`
 * ci-dessus pour `remise_carte.statut_remise`. `en_transit` est posé à la
 * création du mouvement, `receptionne` à la confirmation de réception,
 * `annule` réservé pour un futur usage (annulation d'un mouvement en cours,
 * non exposé par une route dans ce sprint).
 */
export const MOUVEMENT_STATUTS = ['en_transit', 'receptionne', 'annule'];
export const REMISE_STATUTS = ['remise', 'refusee', 'ajournee', 'non_conforme'];
export const CONTROLE_CANAUX = ['mobile', 'web', 'pos'];
export const CONTROLE_METHODES = ['qr', 'nfc', 'manuel', 'biometrie'];
export const CONTROLE_RESULTATS = [
    'valide',
    'invalide',
    'suspendue',
    'expiree',
    'inconnue',
    'incoherence_identite',
    'fraude_suspectee',
];
export const UTILISATEUR_STATUTS = ['actif', 'suspendu', 'bloque', 'archive'];
export const SITE_TYPES = [
    'agence',
    'centre_immatriculation',
    'centre_biometrique',
    'depot',
    'direction',
    'centre_mobile',
];
/**
 * `site_operationnel.statut_site` — colonne texte libre dans le schéma
 * fourni, valeurs fixées ici (même principe que `MOUVEMENT_STATUTS`). Un site
 * fermé passe `inactif` et n'est plus proposé comme destination ; il n'est
 * jamais supprimé, les mouvements qui le référencent devant rester lisibles.
 */
export const SITE_STATUTS = ['actif', 'inactif'];
/**
 * `incident_systeme.niveau_criticite`/`statut_incident` (Sprint 5) : colonnes
 * texte libre dans le schéma fourni (comme `statut_mouvement` ci-dessus),
 * valeurs choisies ici. `niveau_criticite` reprend l'échelle déjà utilisée par
 * `journal_audit.niveau_risque` (normal/eleve/critique) en y ajoutant
 * `faible`, plus fine pour un incident logistique mineur (ex. léger retard).
 */
export const INCIDENT_NIVEAUX_CRITICITE = ['faible', 'moyen', 'eleve', 'critique'];
export const INCIDENT_STATUTS = ['ouvert', 'en_cours', 'resolu', 'clos'];
/**
 * `carte_sociale.type_carte` / `lot_carte.type_lot` — jusqu'ici du texte
 * libre sans valeur contrainte (toujours "standard" en pratique, jamais
 * choisi explicitement). Introduit pour distinguer les deux variantes du bon
 * à tirer approuvé par la CNSS (SADEC, contrat CNSS/DG/CGPMP/F/03/2025,
 * réf. REF-01 "sans puce" / REF-02 "avec puce") : la variante d'un lot reçu
 * du fournisseur détermine celle des cartes qui en sont affectées (voir
 * `POST /cartes` dans `apps/api/src/modules/cartes/cartes.routes.ts`) —
 * jamais choisie librement carte par carte.
 */
export const CARTE_TYPES = ['standard', 'avec_puce'];
export const CARTE_TYPE_LABELS = {
    standard: 'Standard (sans puce)',
    avec_puce: 'Avec puce',
};
//# sourceMappingURL=statuses.js.map